import { defineStore } from 'pinia';
import { useNuxtApp, useRouter } from 'nuxt/app';
import { useAwCacheKeyStore } from '~~/common/stores/awCacheKey';
import { useCategoryStore } from '~~/common/stores/category';
import { useCompareStore } from '~~/common/stores/compare';
import { useConfigStore } from '~~/common/stores/config';
import { useUserStore } from '~~/common/stores/user';
import { useUserInterfaceStore } from '~~/common/stores/userInterface';
import { useTranslationStore } from '~~/common/stores/translation';
import { useDeliveryStore } from '~~/shop/app/stores/delivery';
import { extractResourceFromPath } from '~~/common/utils/urlResource';
import { asObjectCookie, asStringCookie } from '~~/common/utils/cookie';
import { useAwPerf } from '~~/common/composables/useAwPerf';
import { useAuthenticationStore } from '~~/common/stores/authentication';
import { ecomShopPages } from '~~/common/config/EcomPagesConfig';
import { useNotificationBarStore } from '~~/shop/app/stores/notificationBar';
import { useCatalogFooterStore } from '~~/shop/app/stores/catalogFooter';
import { LT_ANON, LT_ECOM } from '~~/common/config/LoginTypeConfig';
import type { LoginType } from '~~/common/config/LoginTypeConfig';
import type { ResponseData, Schema } from '~~/common/types/api';

type LoginTypeResponse = ResponseData<'/api/v2/token/login_type', 'get'>;

const LOGIN_TYPES: readonly LoginType[] = [LT_ANON, LT_ECOM];

function isKnownLoginType (value: string): value is LoginType {
  return (LOGIN_TYPES as readonly string[]).includes(value);
}

// The selected_postcode cookie holds a DeliveryAreaModel.DeliveryAreaInputModel
// ({ type, areaId }) plus a frontend-only `closed` flag; it is parsed from JSON
// so every field is treated defensively.
type SelectedPostCode = Partial<Schema<'DeliveryAreaModel.DeliveryAreaInputModel'>> & { closed?: boolean };

export const useRootStore = defineStore('rootStore', () => {
  const nuxtApp = useNuxtApp();
  const router = useRouter();

  async function nuxtServerInit () {
    const awPerf = useAwPerf();
    awPerf?.markPhase('nuxtServerInitStart');
    const awCacheKeyStore = useAwCacheKeyStore();
    const configStore = useConfigStore();
    const categoryStore = useCategoryStore();
    const compareStore = useCompareStore();
    const userStore = useUserStore();
    const userInterfaceStore = useUserInterfaceStore();
    const deliveryStore = useDeliveryStore();
    const translationStore = useTranslationStore();
    const authenticationStore = useAuthenticationStore();
    const notificationBarStore = useNotificationBarStore();
    const catalogFooterStore = useCatalogFooterStore();
    const {
      $api,
      $i18n,
      $logger,
      $cookies,
    } = nuxtApp;

    const accessToken = asStringCookie($cookies.get('access_token'));
    const refreshToken = asStringCookie($cookies.get('refresh_token'));
    const loginType = asStringCookie($cookies.get('login_type'));

    if (loginType && isKnownLoginType(loginType)) {
      userStore.setLoginType(loginType);
    } else if (accessToken) {
      try {
        const loginTypeResponse = await $api.$get<LoginTypeResponse>('token/login_type');
        // The response type is defensive (DeepPartial), but the swagger documents
        // login_type as always present; guard to keep TS honest.
        if (loginTypeResponse.login_type) {
          userStore.setLoginType(loginTypeResponse.login_type);
          $cookies.set('login_type', loginTypeResponse.login_type, {
            path: '/',
            maxAge: loginTypeResponse.expires_in,
            sameSite: 'lax',
          });
        }
      } catch (error) {
        $logger.error(error);
      }
    } else {
      userStore.setLoginType(LT_ANON);
      $cookies.set('login_type', LT_ANON, {
        path: '/',
        sameSite: 'lax',
      });
    }

    const selectedPostCode = asObjectCookie<SelectedPostCode>($cookies.get('selected_postcode'));
    const loggerHandler = (err: unknown) => {
      $logger.error(err);
    };
    const categoryTreeNeeded = (): boolean => {
      try {
        const resource = extractResourceFromPath(router.currentRoute.value.fullPath);
        const routeName = router.currentRoute.value?.name;
        const isEcomPage = typeof routeName === 'string' && ecomShopPages.includes(routeName);
        return isEcomPage || Boolean(resource);
      } catch (error) {
        $logger.error(error);
        return false;
      }
    };
    if (!accessToken && refreshToken) {
      await authenticationStore.loginWithRefreshToken();
    }

    if (selectedPostCode && !accessToken) {
      if (refreshToken) {
        await authenticationStore.loginWithRefreshToken();
      } else {
        await authenticationStore.loginAnonymous();
      }
    }

    await Promise.all([
      awCacheKeyStore.fetchCacheSegmentation(),
      // todo meg kell nézni, hogy van-e valami nuxt cache-elési lehetőség, de ez csak később
      translationStore.fetchTranslations({ localeCode: $i18n.locale.value }).catch(loggerHandler),
      configStore.fetchConfigCommon().catch(loggerHandler),
      notificationBarStore.fetchNotifications().catch(loggerHandler),
      catalogFooterStore.getCatalogFooterData().catch(loggerHandler),
    ]);

    if (selectedPostCode || accessToken) {
      await deliveryStore.fetchMethod().catch(loggerHandler);
    }

    await Promise.all([
      categoryTreeNeeded() ? categoryStore.fetchCategories().catch(loggerHandler) : Promise.resolve(),
      accessToken ? userStore.fetchUser({ refreshData: false }).catch(loggerHandler) : Promise.resolve(),
    ]);

    userInterfaceStore.setTimeZoneOffsetInHours();

    compareStore.setIsComparePage(false);

    const { type, areaId } = selectedPostCode ?? {};
    if (type && areaId && !selectedPostCode?.closed && !deliveryStore.setup) {
      await deliveryStore.pushMethod({ type, areaId }).catch(loggerHandler);
    }

    if (import.meta.server) {
      global.storeHelper = global?.storeHelper || {};
      global.storeHelper.cdExternalId = useUserStore()?.data?.personalInfo?.cdExternalId ?? undefined;
      global.storeHelper.isEcomUser = useUserStore()?.isEcomUser;
      global.storeHelper.locale = $i18n.locale.value;
    }
    awPerf?.markPhase('nuxtServerInitDone');
  }

  async function clientInit () {
  }

  return {
    nuxtServerInit,
    clientInit,
  };
});
